import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { config, exchanges } from '@/api/endpoints';
import { Notice } from '@/components/Notice';
import { RequirePermission } from '@/components/RequirePermission';
import { describe } from '@/lib/errors';

function policyMode(strategy: { loss_exit_policy: Record<string, unknown> | null }): string {
  const mode = strategy.loss_exit_policy?.mode;
  return typeof mode === 'string' ? mode : 'unknown';
}

export function StrategiesPage() {
  const queryClient = useQueryClient();
  const strategies = useQuery({ queryKey: ['strategies'], queryFn: config.strategies });
  const assignments = useQuery({
    queryKey: ['strategy-assignments'],
    queryFn: config.strategyAssignments,
  });
  const accounts = useQuery({ queryKey: ['exchange-accounts'], queryFn: exchanges.accounts });
  const plugins = useQuery({ queryKey: ['strategy-plugins'], queryFn: config.strategyPlugins });

  const [strategyId, setStrategyId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [newName, setNewName] = useState('');
  const [newPlugin, setNewPlugin] = useState('');
  const [tpStrategyId, setTpStrategyId] = useState('');
  const [tpValue, setTpValue] = useState('');
  const [tpNote, setTpNote] = useState('');
  const [tpDone, setTpDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['strategy-assignments'] });
    void queryClient.invalidateQueries({ queryKey: ['strategies'] });
  };

  // New strategies are created disabled; switching one on is a separate act.
  const create = useMutation({
    mutationFn: () => config.createStrategy({ name: newName.trim(), plugin_key: newPlugin }),
    onSuccess: () => {
      setNewName('');
      setNewPlugin('');
      refresh();
    },
    onError: (err) => setError(describe(err)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enable }: { id: number; enable: boolean }) =>
      enable ? config.enableStrategy(id) : config.disableStrategy(id),
    onSuccess: refresh,
    onError: (err) => setError(describe(err)),
  });

  const assign = useMutation({
    mutationFn: () =>
      config.assignStrategy({
        strategy_id: Number(strategyId),
        exchange_account_id: Number(accountId),
        canonical_symbol: symbol.trim() || null,
      }),
    onSuccess: () => {
      setSymbol('');
      refresh();
    },
    onError: (err) => setError(describe(err)),
  });

  // A new version of the strategy. The take-profit job reads it at every
  // positions check, so open positions move to the new target within minutes.
  const changeTakeProfit = useMutation({
    mutationFn: () => config.changeTakeProfit(Number(tpStrategyId), tpValue.trim(), tpNote.trim()),
    onSuccess: (strategy) => {
      setTpDone(
        `${strategy.name} now sells at ${strategy.take_profit_pct ?? tpValue}% above the entry ` +
          `(version ${String(strategy.active_version ?? '')}). Its open positions move to the ` +
          'new target within about two minutes.',
      );
      setTpValue('');
      setTpNote('');
      refresh();
    },
    onError: (err) => {
      setTpDone(null);
      setError(describe(err));
    },
  });
  const tpRange = (strategies.data ?? [])[0]?.take_profit_range ?? { min: '5', max: '10' };
  const tpChosen = (strategies.data ?? []).find((item) => String(item.id) === tpStrategyId);

  const unassign = useMutation({
    mutationFn: (id: number) => config.unassignStrategy(id),
    onSuccess: refresh,
    onError: (err) => setError(describe(err)),
  });

  const accountLabel = (id: number) => {
    const account = (accounts.data ?? []).find((item) => item.id === id);
    return account ? `${account.exchange_code} · ${account.label}` : `account ${id}`;
  };
  const strategyLabel = (id: number) =>
    (strategies.data ?? []).find((item) => item.id === id)?.name ?? `strategy ${id}`;

  const enabledUnassigned = (strategies.data ?? []).filter(
    (strategy) =>
      strategy.is_enabled &&
      !(assignments.data ?? []).some((a) => a.strategy_id === strategy.id),
  );

  return (
    <>
      <div className="page-head">
        <h1>Strategies</h1>
        <p>
          Strategies are versioned alongside their exit rules. A strategy runs only where it is
          assigned: enabling one is not enough, and an unassigned strategy trades nothing.
        </p>
      </div>

      <section className="panel">
        <RequirePermission permission="config:strategies">
          <div className="panel-body">
            <div className="assign">
              <label>
                <span>New strategy</span>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="name"
                />
              </label>
              <label>
                <span>Plugin</span>
                <select value={newPlugin} onChange={(e) => setNewPlugin(e.target.value)}>
                  <option value="">choose…</option>
                  {(plugins.data ?? []).map((plugin) => (
                    <option key={plugin.key} value={plugin.key} title={plugin.description}>
                      {plugin.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                disabled={!newName.trim() || !newPlugin || create.isPending}
                onClick={() => create.mutate()}
              >
                Create
              </button>
            </div>
          </div>
        </RequirePermission>
        <div className="panel-body flush">
          {(strategies.data ?? []).length === 0 ? (
            <div className="empty">
              No strategies yet. Create one above, or run seed-defaults to add one per shipped
              plugin.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Plugin</th>
                  <th className="right">Version</th>
                  <th>Running</th>
                  <th className="right">Take-profit</th>
                  <th>On a losing position</th>
                  <th className="right">Review after</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(strategies.data ?? []).map((strategy) => {
                  const mode = policyMode(strategy);
                  const holdDays = strategy.loss_exit_policy?.max_holding_days;
                  return (
                    <tr key={strategy.id}>
                      <td>{strategy.name}</td>
                      <td className="num">{strategy.plugin_key}</td>
                      <td className="right num">{strategy.active_version ?? '—'}</td>
                      <td>{strategy.is_enabled ? 'yes' : 'no'}</td>
                      <td className="right num" data-testid={`take-profit-${strategy.id}`}>
                        {strategy.take_profit_pct ? `${strategy.take_profit_pct}%` : '—'}
                      </td>
                      <td style={{ color: mode === 'BLOCK_LOSS_EXITS' ? 'var(--warn)' : undefined }}>
                        {mode === 'BLOCK_LOSS_EXITS'
                          ? 'holds, asks for approval'
                          : mode === 'ALLOW_LOSS_EXITS'
                            ? 'sells at a loss'
                            : mode.toLowerCase().replace(/_/g, ' ')}
                      </td>
                      <td className="right num">{holdDays ? `${String(holdDays)} days` : '—'}</td>
                      <td className="right">
                        <RequirePermission permission="config:strategies">
                          <button
                            type="button"
                            className={strategy.is_enabled ? 'danger' : ''}
                            disabled={toggle.isPending}
                            onClick={() =>
                              toggle.mutate({ id: strategy.id, enable: !strategy.is_enabled })
                            }
                          >
                            {strategy.is_enabled ? 'Disable' : 'Enable'}
                          </button>
                        </RequirePermission>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <RequirePermission permission="config:strategies">
        <section className="panel" data-testid="take-profit-panel">
          <div className="panel-head">
            <h2>Take-profit</h2>
          </div>
          <div className="panel-body">
            <p className="muted">
              How far above its entry a strategy sells each position, after fees. Set between{' '}
              {tpRange.min}% and {tpRange.max}%. Lower sells sooner and more often for less; higher
              waits for bigger moves that fewer positions reach. A change applies to the
              strategy&apos;s open positions too: their resting sells move to the new target at the
              next positions check, within about two minutes.
            </p>
            {tpDone ? <Notice tone="info">{tpDone}</Notice> : null}
            <div className="assign">
              <label>
                <span>Strategy</span>
                <select
                  value={tpStrategyId}
                  onChange={(e) => {
                    setTpStrategyId(e.target.value);
                    setTpDone(null);
                  }}
                >
                  <option value="">choose…</option>
                  {(strategies.data ?? []).map((strategy) => (
                    <option key={strategy.id} value={strategy.id}>
                      {strategy.name}
                      {strategy.take_profit_pct ? ` (now ${strategy.take_profit_pct}%)` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>New take-profit (%)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={tpRange.min}
                  max={tpRange.max}
                  step="0.5"
                  value={tpValue}
                  onChange={(e) => setTpValue(e.target.value)}
                  placeholder={tpChosen?.take_profit_pct ?? tpRange.min}
                />
              </label>
              <label>
                <span>Why are you changing it?</span>
                <input
                  value={tpNote}
                  onChange={(e) => setTpNote(e.target.value)}
                  placeholder="e.g. choppy market, take smaller gains"
                />
              </label>
              <button
                type="button"
                disabled={
                  !tpStrategyId || !tpValue.trim() || tpNote.trim().length < 5 ||
                  changeTakeProfit.isPending
                }
                onClick={() => changeTakeProfit.mutate()}
              >
                Save take-profit
              </button>
            </div>
          </div>
        </section>
      </RequirePermission>

      <section className="panel">
        <div className="panel-head">
          <h2>Where strategies run</h2>
        </div>
        <div className="panel-body">
          {error ? <div className="notice notice--error">{error}</div> : null}

          {enabledUnassigned.length > 0 ? (
            <div className="notice notice--warn">
              {enabledUnassigned.length === 1
                ? `"${enabledUnassigned[0]?.name ?? ''}" is enabled but assigned to no account, so it trades nothing.`
                : `${enabledUnassigned.length} enabled strategies are assigned to no account, so they trade nothing.`}
            </div>
          ) : null}

          <RequirePermission permission="config:strategies">
            <div className="assign">
              <label>
                <span>Strategy</span>
                <select value={strategyId} onChange={(e) => setStrategyId(e.target.value)}>
                  <option value="">choose…</option>
                  {(strategies.data ?? []).map((strategy) => (
                    <option key={strategy.id} value={strategy.id}>
                      {strategy.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Account</span>
                <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                  <option value="">choose…</option>
                  {(accounts.data ?? []).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.exchange_code} · {account.label} ({account.mode.toLowerCase()})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Symbol (optional)</span>
                <input
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  placeholder="all candidates, or COIN/USDT"
                />
              </label>
              <button
                type="button"
                disabled={!strategyId || !accountId || assign.isPending}
                onClick={() => assign.mutate()}
              >
                Assign
              </button>
            </div>
          </RequirePermission>

          {(assignments.data ?? []).length === 0 ? (
            <div className="empty">
              Nothing is assigned yet, so no strategy will place an order.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th>Account</th>
                  <th>Symbol</th>
                  <th>Enabled</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(assignments.data ?? []).map((assignment) => (
                  <tr key={assignment.id} data-testid={`assignment-${assignment.id}`}>
                    <td>{strategyLabel(assignment.strategy_id)}</td>
                    <td>{accountLabel(assignment.exchange_account_id)}</td>
                    <td className="num">{assignment.canonical_symbol ?? 'all candidates'}</td>
                    <td>{assignment.is_enabled ? 'yes' : 'no'}</td>
                    <td className="right">
                      <RequirePermission permission="config:strategies">
                        <button
                          type="button"
                          className="danger"
                          disabled={unassign.isPending}
                          onClick={() => unassign.mutate(assignment.id)}
                        >
                          Unassign
                        </button>
                      </RequirePermission>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h2>What "holds, asks for approval" means</h2>
        </div>
        <div className="panel-body">
          <p>
            A strategy set to hold will not sell a position that is underwater. A triggered stop
            raises an alert and requests approval instead of selling, so losses stay open until
            someone decides otherwise or the review period expires.
          </p>
          <p style={{ color: 'var(--text-secondary)' }}>
            Position sizing is therefore the only automatic limit on how much a single bad entry can
            cost. The shipped risk limits are deliberately small for that reason.
          </p>
        </div>
      </section>
    </>
  );
}
